import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Cross1Icon, DragHandleDots2Icon } from '@radix-ui/react-icons';
import { useEffect, useMemo, useState, type JSX } from 'react';

import { Badge } from '../../../primitives/badge';
import { Button } from '../../../primitives/button';
import { cn } from '../../../utils/cn';
import { ComboboxModel, type ComboboxModelModel } from '../../combobox/combobox-model';

export type MultiSelectorComboboxModelItem = {
  badge?: string | JSX.Element;
  button?: {
    icon?: JSX.Element;
    onClick: VoidFunction;
    text?: string;
  };
  disabled?: boolean;
  item: string | JSX.Element;
  key?: string;
  meta?: unknown;
  value: string;
};

interface MultiSelectorComboboxProps {
  className?: string;
  comboboxClassName?: string;
  disabled?: boolean;
  enableDragAndDrop?: boolean;
  emptyOptionsText?: string;
  id?: string;
  model: MultiSelectorComboboxModelItem[];
  onChange?: (selected: MultiSelectorComboboxModelItem[]) => void;
  placeholder?: string;
  value?: MultiSelectorComboboxModelItem[];
}

interface SortableBadgeProps {
  disabled?: boolean;
  item: MultiSelectorComboboxModelItem;
  onRemove: (value: string) => void;
  showDragHandler?: boolean;
}

function SortableBadge({ disabled, item, onRemove, showDragHandler = false }: SortableBadgeProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.value,
    disabled: disabled || !showDragHandler,
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Badge
        className={cn('h-8 gap-2 rounded-full p-1', {
          'pl-4': !showDragHandler,
        })}
        variant="secondary"
      >
        {showDragHandler ? (
          <Button
            size="icon-sm"
            variant="ghost"
            className={cn('size-6', disabled && 'hidden')}
            title="Arrastrar"
            onMouseDown={(event) => {
              event.stopPropagation();
            }}
            {...attributes}
            {...listeners}
          >
            <DragHandleDots2Icon className="size-3.5!" />
          </Button>
        ) : null}

        <span className="inline-flex items-center">{item.badge ?? item.item}</span>

        <Button
          size="icon-sm"
          variant="ghost"
          className={cn('size-6', disabled && 'hidden')}
          title="Eliminar"
          onClick={() => onRemove(item.value)}
        >
          <Cross1Icon className="size-3.5!" />
        </Button>
      </Badge>
    </div>
  );
}

function isSameSelection(
  current: MultiSelectorComboboxModelItem[],
  next: MultiSelectorComboboxModelItem[],
): boolean {
  if (current.length !== next.length) return false;

  return current.every((item, index) => item.value === next[index]?.value);
}

export function MultiSelectorCombobox({
  className,
  comboboxClassName,
  disabled,
  enableDragAndDrop = false,
  emptyOptionsText,
  id,
  model,
  onChange,
  placeholder = 'Seleccionar',
  value,
}: MultiSelectorComboboxProps) {
  const [selected, setSelected] = useState<MultiSelectorComboboxModelItem[]>(value ?? []);
  const [comboboxValue, setComboboxValue] = useState<string | undefined>();

  useEffect(() => {
    if (!Array.isArray(value)) return;

    setSelected((previousValue) => (isSameSelection(previousValue, value) ? previousValue : value));
  }, [value]);

  const selectedValues = useMemo(() => selected.map((item) => item.value), [selected]);

  const availableModel = useMemo<ComboboxModelModel>(
    () =>
      model
        .filter((item) => !selectedValues.includes(item.value))
        .map((item) => ({
          button: item.button,
          item: item.item,
          key: item.key,
          value: item.value,
        })),
    [model, selectedValues],
  );

  const selectedByValue = useMemo(() => new Map(model.map((item) => [item.value, item])), [model]);
  const isDragAndDropEnabled = enableDragAndDrop && !disabled;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const pushChange = (items: MultiSelectorComboboxModelItem[]) => {
    setSelected(items);
    onChange?.(items);
  };

  const handleRemove = (valueToRemove: string) => {
    pushChange(selected.filter((item) => item.value !== valueToRemove));
  };

  const handleSelect = (selectedValue: string) => {
    const selectedItem = selectedByValue.get(selectedValue);
    if (!selectedItem || selectedItem.disabled) {
      setComboboxValue(undefined);
      return;
    }

    if (selected.some((item) => item.value === selectedItem.value)) {
      setComboboxValue(undefined);
      return;
    }

    pushChange([...selected, selectedItem]);
    setComboboxValue(undefined);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    if (!isDragAndDropEnabled) return;

    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = selected.findIndex((item) => item.value === active.id);
    const newIndex = selected.findIndex((item) => item.value === over.id);

    if (oldIndex < 0 || newIndex < 0) return;

    pushChange(arrayMove(selected, oldIndex, newIndex));
  };

  return (
    <div className={cn('rounded-[1.1rem] border border-input p-1', className)}>
      <div className="flex flex-wrap items-center gap-1">
        {isDragAndDropEnabled ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={selectedValues} strategy={rectSortingStrategy}>
              {selected.map((item) => (
                <SortableBadge
                  key={item.value}
                  disabled={disabled}
                  item={item}
                  onRemove={handleRemove}
                  showDragHandler={isDragAndDropEnabled}
                />
              ))}
            </SortableContext>
          </DndContext>
        ) : (
          selected.map((item) => (
            <SortableBadge
              key={item.value}
              disabled={disabled}
              item={item}
              onRemove={handleRemove}
              showDragHandler={false}
            />
          ))
        )}

        <div className="min-w-[25rem] flex-1">
          <ComboboxModel
            id={id}
            className={cn('pr-3', comboboxClassName)}
            classNameCommandList="max-h-[min(16rem,55vh)]"
            disabled={disabled}
            emptyOptionsText={emptyOptionsText}
            model={availableModel}
            placeholder={placeholder}
            value={comboboxValue}
            variant="ghost"
            size="sm"
            onSelect={handleSelect}
          />
        </div>
      </div>
    </div>
  );
}
